import styled from '@emotion/styled'
import { colors, fonts } from 'component-library/__styling/settings'
import { IconSearch } from 'component-library/icons/IconSearch/IconSearch'
import { calcRem, breakpoint } from 'component-library/__styling/style-functions'
import { conditionalStyles } from 'component-library/__styling/conditional-styles'
import { withClass } from 'common/emotion'
import { IconCross } from 'component-library/icons/IconCross/IconCross'

export const searchContentsClassName = 'search'
export const searchIconClassName = 'search-icon'
export const searchFormClassName = 'search-form'
export const searchInputClassName = 'search-input'
export const clearButtonClassName = 'search-btn-clear'
export const suggestionsClassName = 'search-suggestions'
export const noSearchResultsClassName = 'search-noresults'

interface StyledSearchContentProps {
    searchInMainNav?: boolean
    searchHasValue: boolean
}

interface StyledSearchSubmitButtonProps {
    disabled: boolean
}

interface StyledClearButtonProps {
    searchHasValue: boolean
}

export const StyledSearchContainer = withClass(searchContentsClassName)(
    styled('div')<StyledSearchContentProps>(({ searchInMainNav }) => ({
        display: 'flex',
        alignItems: 'center',
        overflow: 'hidden',

        ...conditionalStyles(searchInMainNav, {
            display: 'none',

            [breakpoint('sm')]: {
                display: 'flex',
                alignItems: 'center',
                position: 'relative',
            },
        }),
    }))
)

export const StyledSearchIcon = withClass(searchIconClassName)(
    styled(IconSearch)(({ theme }) => ({
        width: calcRem(14),
        height: calcRem(14),
        fill: theme.sectionValues.primaryColor,
        display: 'flex',
    }))
)
StyledSearchIcon.displayName = 'SearchIcon'

export const StyledCrossIcon = styled(IconCross)({
    height: calcRem(10),
    width: calcRem(10),
    fill: colors.thewest.greyGorilla,
})
StyledCrossIcon.displayName = 'IconCross'

interface StyledSearchFormProps {
    searchHasFocus: boolean
    searchHasValue: boolean
    isLarge: boolean
}

export const StyledSearchForm = withClass(searchFormClassName)(
    styled('form')<StyledSearchFormProps>(({ isLarge, searchHasFocus, searchHasValue }) => ({
        display: 'flex',
        padding: isLarge ? calcRem(3, 3, 3, 10) : calcRem(1, 1, 1, 10),
        fontSize: isLarge ? calcRem(16) : calcRem(13),

        margin: 0,
        borderRadius: '1rem',
        overflow: 'hidden',
        width: '100%',
        background: colors.thewest.greyErmine,
        border:
            searchHasFocus || searchHasValue
                ? `1px solid ${colors.thewest.greyDolphin}`
                : '1px solid transparent',
        color: searchHasFocus ? colors.black : colors.thewest.greyElephant,
    }))
)
StyledSearchForm.displayName = 'SearchForm'

export const StyledSearchInput = withClass(searchInputClassName)(
    styled('input')({
        width: '100%',
        border: 'none',
        fontFamily: fonts.thewest.sansSerif,
        fontWeight: 600,
        background: 'transparent',

        '&:focus': {
            outline: 'none',
        },

        '&::placeholder': {
            color: colors.thewest.greyElephant,
            opacity: 1,
        },
    })
)
StyledSearchInput.displayName = 'SearchInput'

export const StyledClearButton = withClass(clearButtonClassName)(
    styled('button')<StyledClearButtonProps>(({ searchHasValue, theme }) => ({
        background: 'transparent',
        cursor: 'pointer',
        border: 'none',
        lineHeight: 0,
        marginRight: calcRem(5),

        '&:hover path, &:focus path': {
            fill: theme.sectionValues.primaryColor,
        },

        [breakpoint('md')]: {
            marginRight: 0,
        },

        ...conditionalStyles(!searchHasValue, {
            visibility: 'hidden',
        }),
    }))
)
StyledClearButton.displayName = 'ClearButton'

export const StyledSearchSubmitButton = styled('button')<StyledSearchSubmitButtonProps>(
    ({ theme }) => ({
        padding: calcRem(2, 6),
        border: 'none',
        borderRadius: calcRem(2),
        cursor: 'pointer',
        margin: calcRem(1),
        outline: 'none',
        backgroundColor: 'transparent',

        [`.${searchIconClassName}`]: {
            fill: colors.thewest.greyElephant,
        },

        '&:disabled': {
            cursor: 'not-allowed',

            [`.${searchIconClassName}`]: {
                fill: colors.thewest.greyElephant,
            },
        },

        [`&:hover:disabled .${searchIconClassName}`]: {
            fill: colors.thewest.greyElephant,
        },

        [`&:hover .${searchIconClassName}`]: {
            fill: theme.sectionValues.primaryColor,
        },

        [`&:focus .${searchIconClassName}`]: {
            fill: theme.sectionValues.primaryColor,
        },
    })
)
StyledSearchSubmitButton.displayName = 'SubmitButton'
